import { Chip, type ChipProps } from '@mui/material';

type ExecutionStatus =
  | 'Succeeded'
  | 'Failed'
  | 'Running'
  | 'Pending'
  | 'Cancelled'
  | 'Timeout'
  | string;

interface StatusChipProps {
  /** Execution or job status */
  status: ExecutionStatus;
  /** Chip size */
  size?: ChipProps['size'];
  /** Click handler */
  onClick?: () => void;
  /** Custom color mapping */
  colorMap?: Record<string, ChipProps['color']>;
}

const DEFAULT_STATUS_COLORS: Record<string, ChipProps['color']> = {
  Succeeded: 'success',
  Failed: 'error',
  Running: 'info',
  Pending: 'warning',
  Cancelled: 'default',
  Timeout: 'error',
};

/**
 * Reusable status chip component with consistent color mapping
 *
 * Automatically maps common execution statuses to appropriate colors.
 * Defaults to 'default' color for unknown statuses.
 *
 * @example
 * <StatusChip status="Succeeded" />
 * <StatusChip status="Failed" size="small" />
 * <StatusChip status="Running" onClick={() => navigate('/execution/123')} />
 */
export const StatusChip = ({
  status,
  size = 'small',
  onClick,
  colorMap = DEFAULT_STATUS_COLORS,
}: StatusChipProps) => {
  const color = colorMap[status] || 'default';

  return (
    <Chip
      label={status}
      size={size}
      color={color}
      onClick={onClick}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
      }}
    />
  );
};
